import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { Play, Loader2, Lock, Crown, ArrowRight, Radar, Zap, ChevronDown, ChevronUp, CheckCircle, Mail, Download, Clock, Shield } from 'lucide-react';
import { SCAN_TILES } from './sections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const sevColor = { critical: 'border-severity-critical/30 text-severity-critical bg-severity-critical/10', high: 'border-severity-high/30 text-severity-high bg-severity-high/10', medium: 'border-severity-medium/30 text-severity-medium bg-severity-medium/10', low: 'border-severity-low/30 text-severity-low bg-severity-low/10', informational: 'border-severity-info/30 text-severity-info bg-severity-info/10' };

export const ScannerWidget = ({ target, setTarget, scanType, setScanType, scanning, onScan, usage }) => {
  const navigate = useNavigate();
  const current = SCAN_TILES.find(t => t.id === scanType) || SCAN_TILES[0];
  if (usage.exhausted) {
    return (
      <Card className="beam-border border-primary/40 bg-primary/5" data-testid="free-limit-reached-card">
        <CardContent className="p-8 flex flex-col md:flex-row items-center gap-6">
          <div className="p-4 rounded-2xl bg-primary/10"><Lock className="h-10 w-10 text-primary" /></div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-lg md:text-xl font-heading font-bold">You've used all {usage.limit} free scans</h2>
            <p className="text-sm text-muted-foreground mt-1">Continue with a paid plan: unlimited targets, AI analysis & remediation, rich 7-section reports, engagements and compliance mapping.</p>
          </div>
          <Button size="lg" className="rounded-full gap-2 btn-glow" onClick={() => navigate('/plans')} data-testid="free-limit-upgrade-btn">Choose a plan <ArrowRight className="h-4 w-4" /></Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="glass-panel rounded-3xl p-3 md:p-4 shadow-2xl shadow-indigo-500/10" data-testid="free-scan-form">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-muted/60 md:w-56 shrink-0">
          <current.icon className="h-4 w-4 text-primary shrink-0" />
          <select value={scanType} onChange={e => setScanType(e.target.value)} className="bg-transparent text-sm font-medium outline-none w-full" data-testid="free-scan-type-select">
            {SCAN_TILES.map(t => <option key={t.id} value={t.id}>{t.label.replace(' Scanner', '').replace(' Scan', '')}</option>)}
          </select>
        </div>
        <Input value={target} onChange={e => setTarget(e.target.value)} onKeyDown={e => e.key === 'Enter' && onScan()} placeholder="https://app.example.com · 203.0.113.10 · com.acme.app" className="h-12 rounded-2xl text-base bg-background/80" data-testid="free-target-input" />
        <Button onClick={onScan} disabled={scanning} className="h-12 rounded-2xl px-6 gap-2 btn-glow shrink-0" data-testid="free-start-scan-btn">
          {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}{scanning ? 'Scanning…' : 'Scan for free'}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-2 pt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Radar className="h-3.5 w-3.5 text-primary" /><span data-testid="free-usage-count">{usage.used} / {usage.limit}</span> free scans used</span>
        <span className="hidden sm:inline">·</span><span>No sign-up</span><span className="hidden sm:inline">·</span><span>Results in seconds</span>
        <button className="ml-auto text-primary hover:underline" onClick={() => navigate('/plans')} data-testid="free-upgrade-btn">Need more? See plans</button>
      </div>
    </div>
  );
};

const FindingRow = ({ f, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="rounded-xl border bg-card overflow-hidden" data-testid={`free-finding-${f.vuln_id}`}>
      <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors" onClick={() => setOpen(o => !o)}>
        <Badge variant="outline" className={`shrink-0 text-[10px] uppercase ${sevColor[f.severity]}`}>{f.severity}</Badge>
        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{f.title}</p><p className="text-[11px] text-muted-foreground font-mono truncate">{f.method} {f.path} · {f.cwe_id} · CVSS {f.cvss_score}</p></div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      <AnimatePresence>{open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="px-3 pb-3 space-y-2 text-sm border-t">
          <p className="text-muted-foreground pt-3">{f.description}</p>
          <div><p className="text-xs font-semibold mb-1">Remediation</p><p className="text-xs text-muted-foreground">{f.issue_remediation}</p></div>
          <p className="text-[11px] text-muted-foreground">{f.owasp_category} · Confidence: {f.confidence}</p>
        </motion.div>
      )}</AnimatePresence>
    </motion.div>
  );
};

export const ReportDialog = ({ open, onOpenChange, scan, visitorId }) => {
  const [form, setForm] = useState({ email: '', name: '', company: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email.includes('@')) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/scans/${scan.scan_id}/report`, { visitor_id: visitorId, ...form }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = `vaptara-report-${scan.scan_id}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success(r.headers['x-report-emailed'] === 'true' ? `Report emailed to ${form.email} and downloaded` : 'Report downloaded — a copy will be emailed once delivery is configured');
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not generate report'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-heading">Get your PDF report</DialogTitle><DialogDescription>We'll email the full report for <span className="font-mono">{scan?.target}</span> and start your download.</DialogDescription></DialogHeader>
        <div className="space-y-3 pt-2">
          <div><Label className="mb-1.5 block">Work email *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@company.com" data-testid="report-email-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1.5 block">Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="report-name-input" /></div>
            <div><Label className="mb-1.5 block">Company</Label><Input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} data-testid="report-company-input" /></div>
          </div>
          <Button className="w-full rounded-full gap-2" onClick={submit} disabled={busy} data-testid="report-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Email & download PDF</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ScanResult = ({ result, onReport }) => {
  const navigate = useNavigate();
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="border-primary/30 overflow-hidden" data-testid="free-scan-result">
        <div className="h-1 bg-gradient-cool" />
        <CardHeader className="pb-2 flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base font-heading flex items-center gap-2"><CheckCircle className="h-5 w-5 text-severity-low" /> {result.total_findings} findings on <span className="font-mono text-primary">{result.target}</span></CardTitle>
          <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={onReport} data-testid="email-report-btn"><Mail className="h-4 w-4" /> Email me the PDF</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-5 gap-2">
            {[['critical', 'bg-severity-critical/10 text-severity-critical'], ['high', 'bg-severity-high/10 text-severity-high'], ['medium', 'bg-severity-medium/10 text-severity-medium'], ['low', 'bg-severity-low/10 text-severity-low'], ['informational', 'bg-severity-info/10 text-severity-info']].map(([s, c]) => (
              <div key={s} className={`text-center p-2.5 rounded-xl ${c}`}><p className="text-lg font-heading font-bold">{result[`${s}_count`]}</p><p className="text-[10px] text-muted-foreground capitalize">{s === 'informational' ? 'Info' : s}</p></div>
            ))}
          </div>
          <div className="space-y-2">{result.findings.map((f, i) => <FindingRow key={f.vuln_id} f={f} i={i} />)}</div>
          <div className="p-4 rounded-2xl border border-dashed border-primary/30 bg-primary/5 flex flex-col sm:flex-row items-center gap-3">
            <Zap className="h-5 w-5 text-primary shrink-0" />
            <p className="text-sm flex-1"><span className="font-medium">Want more?</span> Paid plans unlock request/response evidence, AI analysis, false-positive validation and exportable reports.</p>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate('/plans')} data-testid="result-upgrade-btn"><Crown className="h-3.5 w-3.5 mr-1" /> View plans</Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export const ScanHistory = ({ history, onOpen }) => history.length === 0 ? null : (
  <Card data-testid="free-scan-history">
    <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Your free scans</CardTitle></CardHeader>
    <CardContent className="space-y-1.5">
      {history.map(s => (
        <button key={s.scan_id} onClick={() => onOpen(s.scan_id)} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left" data-testid={`free-history-${s.scan_id}`}>
          <Shield className="h-4 w-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0"><p className="text-sm font-medium">{s.scan_type?.toUpperCase()}</p><p className="text-[11px] text-muted-foreground font-mono truncate">{s.target}</p></div>
          <span className="text-xs text-muted-foreground">{s.total_findings} findings</span>
          <span className="text-[11px] text-muted-foreground hidden sm:flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(s.started_at).toLocaleDateString()}</span>
        </button>
      ))}
    </CardContent>
  </Card>
);
